import type { PunchPhotoInput } from '@/lib/apiClient';
import { uploadToJobFolder, GraphConsentError } from '@/lib/graphUpload';
import { isDefiniteRejection, isConflictError } from '@/lib/httpErrors';
import { getDeviceLocation, deviceFixApplies, localYmd } from '@/lib/photoMeta';
import { punchPhotoName, SIGN_IN_FAILED } from './punchPhotos';

/**
 * Video clips on punch items (#518 phase 2): always allowed, no per-job
 * switch. A clip is extra evidence only — the before photo starts an item and
 * an after photo marks it fixed; a video stands in for neither, so every
 * photo rule and the sub sheets are unchanged. The close-out report, which
 * can't play a clip, says "N videos in the Job Folder" (videoCountLine).
 *
 * Recorded with the phone's own camera, as photos are, and uploaded to the
 * Job Folder as is (no re-encoding in the browser) with the user's own Graph
 * token — in chunks, so a long clip shows its progress.
 */

/** Clips are kept short: they upload from site over a phone signal. */
export const VIDEO_MAX_SECONDS = 60;
/** A backstop for a clip whose length this browser can't read. */
export const VIDEO_MAX_BYTES = 500 * 1024 * 1024;

/** Extensions a video can have; a clip keeps its own (an AVI stays .avi). */
const VIDEO_EXT = /\.(mp4|m4v|mov|webm|3gp|3g2|avi|mkv|wmv|mpg|mpeg|mts|m2ts|ogv)$/i;
/** For a clip with no extension (some Android galleries), by its type. */
const EXT_BY_TYPE: Record<string, string> = {
  'video/mp4': 'mp4', 'video/quicktime': 'mov', 'video/webm': 'webm', 'video/3gpp': '3gp', 'video/3gpp2': '3g2',
  'video/x-msvideo': 'avi', 'video/x-matroska': 'mkv', 'video/x-ms-wmv': 'wmv', 'video/mpeg': 'mpg', 'video/ogg': 'ogv',
};

/**
 * The clip's length in seconds, from its own metadata — or null when this
 * browser can't read it (an HEVC .mov on some desktop browsers): the size cap
 * then stands in for the length check.
 */
export function readVideoDuration(file: File, timeoutMs = 10_000): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return resolve(null);
    const v = document.createElement('video');
    const url = URL.createObjectURL(file);
    let done = false;
    const finish = (n: number | null) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(n);
    };
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    v.preload = 'metadata';
    v.muted = true;
    v.onloadedmetadata = () => finish(Number.isFinite(v.duration) && v.duration > 0 ? v.duration : null);
    v.onerror = () => finish(null);
    v.src = url;
  });
}

/** Why a picked clip can't be added, in plain words — or null when it can. */
export function videoProblem(file: File, seconds: number | null): string | null {
  if (!file.type.startsWith('video/') && !VIDEO_EXT.test(file.name)) return 'That file isn’t a video.';
  // Named for what it is, or not at all: a made-up ".mp4" on another format
  // would be served and played as MP4 and fail (Codex on #593).
  if (!videoExtension(file)) return 'This video’s format can’t be told from the file. Save it as an MP4 or MOV and choose it again.';
  // Half a second of grace: phones stop a "60-second" clip a few frames late.
  if (seconds != null && seconds > VIDEO_MAX_SECONDS + 0.5) {
    return `That clip is ${Math.round(seconds)} seconds long. Keep clips to ${VIDEO_MAX_SECONDS} seconds or less — trim it, or record a shorter one.`;
  }
  if (file.size > VIDEO_MAX_BYTES) return 'That clip is over 500 MB. Record a shorter one.';
  return null;
}

/**
 * The clip's own extension, from its name, or else from its type — never a
 * guess (Codex on #593). Null when neither says: videoProblem turns that
 * clip away rather than upload it under a fabricated name.
 */
export function videoExtension(file: File): string | null {
  const fromName = VIDEO_EXT.exec(file.name)?.[1];
  if (fromName) return fromName.toLowerCase();
  return EXT_BY_TYPE[file.type] ?? null;
}

/** "42 MB", "3.5 MB" — a clip's size for a label. */
export function fileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** "0:42", "1:00" — a clip's length for a label. */
export function clipLength(seconds: number | null): string {
  if (seconds == null) return '';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Upload one clip for an item. As with photos, pass the same `tag` on every
 * attempt: the name is then the same each time, and with 'replace' a retry
 * after a failed or lost reply overwrites its own earlier copy.
 *
 * When and where it was filmed are known only for a clip just recorded
 * (Record a Video): it was filmed now, here, so the phone's position stands
 * in, as for a fresh photo with no GPS. A clip chosen from a library or a
 * folder carries neither — its last-modified time is when it was copied, not
 * filmed, so trusting it stamped a clip emailed yesterday with today's date
 * and the office's location (review). The position is asked for alongside
 * the upload, not before it: indoors a fix can take its full 10 seconds, and
 * the clip shouldn't sit at 0% waiting for a label.
 */
export async function uploadPunchVideo(o: {
  file: File;
  itemNo: number;
  jobNumber: string;
  jobFolderUrl: string;
  subfolder: string;
  getToken: () => Promise<string>;
  tag: string;
  /** Just recorded with the camera (Record a Video), not chosen from a library or folder. */
  recorded: boolean;
  onProgress?: (sent: number, total: number) => void;
}): Promise<PunchPhotoInput> {
  const takenAt = o.recorded ? new Date(o.file.lastModified || Date.now()) : null;
  const fix = o.recorded && deviceFixApplies(takenAt) ? getDeviceLocation() : Promise.resolve(null);
  const name = punchPhotoName({
    jobNumber: o.jobNumber,
    itemNo: o.itemNo,
    kind: 'video',
    // A chosen clip's file is named for the day it was added.
    ymd: localYmd(takenAt ?? new Date()),
    // videoProblem has turned away a clip with no known extension.
    ext: videoExtension(o.file) ?? 'mp4',
    tag: o.tag,
  });
  const res = await uploadToJobFolder({
    getToken: o.getToken,
    jobFolderUrl: o.jobFolderUrl,
    subfolder: o.subfolder,
    fileName: name,
    file: o.file,
    conflictBehavior: 'replace',
    onProgress: o.onProgress,
  });
  const device = await fix;   // never rejects: no fix is null
  return {
    fileName: res.name,
    webUrl: res.webUrl,
    sizeBytes: res.size,
    takenAt: takenAt?.toISOString(),
    latitude: device ? Number(device.latitude.toFixed(6)) : undefined,
    longitude: device ? Number(device.longitude.toFixed(6)) : undefined,
    locationSource: device ? 'device' : undefined,
    locationAccuracyM: device?.accuracyM,
  };
}

/**
 * Attach an uploaded clip to its item, with one more try after a dropped or
 * 5xx reply. Adding it again is safe (the server answers a repeat with the
 * clip it already has), and without the retry a passing blip after a long
 * upload meant uploading the whole clip again — or, on the phone, leaving
 * the file in the Job Folder attached to nothing (review). A refusal is final.
 * Shared by the item's + Add a video and the phone's add flow.
 */
export async function attachClip<T>(addMedia: () => Promise<T>): Promise<T> {
  try {
    return await addMedia();
  } catch (e) {
    if (isDefiniteRejection(e) || isConflictError(e)) throw e;
    return addMedia();
  }
}

/**
 * The phone add's optional clip, put on the item it just saved (#518 phase
 * 2). The item is saved either way: null when the clip went on, otherwise
 * what to tell the user. A sign-in failure reads as SIGN_IN_FAILED, as it
 * does everywhere else, not MSAL's raw text (review).
 */
export async function addClipToSavedItem(o: {
  item: { id: number; itemNo: number; label: string };
  file: File;
  upload: (file: File, itemNo: number) => Promise<PunchPhotoInput>;
  addMedia: (itemId: number, clip: PunchPhotoInput) => Promise<unknown>;
}): Promise<string | null> {
  try {
    const clip = await o.upload(o.file, o.item.itemNo);
    await attachClip(() => o.addMedia(o.item.id, clip));
    return null;
  } catch (e) {
    const saved = `${o.item.label} was saved, but its video didn’t upload`;
    const after = 'Add the video from the item — the clip is still on your phone.';
    if (e instanceof GraphConsentError) return `${saved}. ${SIGN_IN_FAILED} ${after}`;
    const why = (e as Error | null)?.message;
    return `${saved}${why ? ` (${why})` : ''}. ${after}`;
  }
}
